import { useMemo } from 'react';

import { useQuery } from 'react-query';

import { getSharedFilters } from './useTimelineQueries';
import { TimelineCalendarWindow } from '../calendarTypes';
import { fetchTimelineCalendarDays } from '../services/calendar';
import { TimelineFilters } from '../timelineData';

interface UseTimelineCalendarDaysProps extends TimelineCalendarWindow {
  enabled: boolean;
  operationId?: string;
  userId: number | null;
  search: string;
  filters: TimelineFilters;
}

/**
 * Dias com eventos na janela visível. As marcas respeitam busca e filtros
 * ativos, exceto o próprio período selecionado no calendário.
 */
export default function useTimelineCalendarDays({
  enabled,
  operationId,
  userId,
  start,
  end,
  search,
  filters,
}: UseTimelineCalendarDaysProps) {
  const {
    dateStart: _dateStart,
    dateEnd: _dateEnd,
    ...calendarFilters
  } = getSharedFilters(search, filters, operationId);
  const operationScope = operationId?.trim() || null;
  const queryEnabled = enabled && start <= end;
  const query = useQuery({
    queryKey: [
      'timeline-calendar',
      operationScope,
      userId,
      start,
      end,
      calendarFilters,
    ],
    queryFn: ({ signal }) =>
      fetchTimelineCalendarDays({ ...calendarFilters, start, end, signal }),
    enabled: queryEnabled,
    staleTime: 30_000,
  });
  /**
   * Marcas só valem para a busca, os filtros e a janela atuais. Enquanto a
   * consulta está desabilitada (busca pendente) ou ainda não respondeu aos
   * critérios novos — inclusive ao trocar de mês —, nenhuma marca é exposta.
   */
  const isCurrent = queryEnabled && query.isSuccess && !query.isPreviousData;
  const currentData = isCurrent ? query.data : undefined;
  const eventDays = useMemo(
    () =>
      new Set(
        (currentData ?? [])
          .filter((day) => day.count > 0)
          .map((day) => day.date),
      ),
    [currentData],
  );

  const isError = queryEnabled && query.isError;

  return {
    eventDays,
    hasData: isCurrent,
    isLoading: !isCurrent && !isError,
    isError,
    retry: query.refetch,
  };
}
